import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CircularProgress from '@mui/material/CircularProgress';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import brandGuideService from '../../services/brandGuideService';
import { IBrandGuideSection } from '../../types/damTypes';
import { API_BASE_URL } from '../../config';

// Fontana brand colors
const COLORS = {
  olympia: '#1B3967',
  originalet: '#0050E6',
  sand: '#EDE8DC',
  sandLight: '#F5F0E8',
};

const ICON_OPTIONS = [
  { value: 'BrandingWatermark', label: 'Logotyp' },
  { value: 'Palette', label: 'Färger' },
  { value: 'TextFields', label: 'Typsnitt' },
  { value: 'Image', label: 'Bildspråk' },
  { value: 'Category', label: 'Pictogram & Ikoner' },
  { value: 'RecordVoiceOver', label: 'Tonalitet' },
  { value: 'Style', label: 'Grafiska element' },
  { value: 'CollectionsBookmark', label: 'Övrigt' },
];

interface BrandGuideManagementPageProps {
  initialSlug?: string | null;
  onInitialSlugHandled?: () => void;
}

interface SectionFormState {
  title: string;
  slug: string;
  icon: string;
  previewText: string;
  introText: string;
  displayOrder: number;
  isPublished: boolean;
  contentHtml: string;
  images: string[];
  // Kortdesign
  cardGradientStart: string;
  cardGradientEnd: string;
  cardSymbol: string;
  cardImage: string;
}

const EMPTY_FORM: SectionFormState = {
  title: '',
  slug: '',
  icon: ICON_OPTIONS[0].value,
  previewText: '',
  introText: '',
  displayOrder: 0,
  isPublished: false,
  contentHtml: '',
  images: [],
  cardGradientStart: '#1B3967',
  cardGradientEnd: '#0050E6',
  cardSymbol: '◆',
  cardImage: '',
};

const BrandGuideManagementPage: React.FC<BrandGuideManagementPageProps> = ({
  initialSlug,
  onInitialSlugHandled,
}) => {
  const [sections, setSections] = useState<IBrandGuideSection[]>([]);
  const [_loading, setLoading] = useState(false);
  const [pdfInfo, setPdfInfo] = useState<{ exists: boolean; url?: string; size?: number; updatedAt?: string } | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<SectionFormState>(EMPTY_FORM);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Inställningar för header/footer
  const [headerSettings, setHeaderSettings] = useState({
    headerSubtitle: 'FONTANA • EST. 1978',
    headerTitle: 'Grafisk Manual',
    headerDescription: '',
    footerText: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Export function
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      
      const response = await brandGuideService.exportData();
      
      // Skapa nedladdningslänk
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brand-guide-export-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setError(null);
    } catch (err: any) {
      console.error('Export error:', err);
      setError('Kunde inte exportera Grafisk Manual');
    } finally {
      setExporting(false);
    }
  };

  // Import function
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Återställ input så samma fil kan väljas igen
    event.target.value = '';
    
    if (!file.name.endsWith('.zip')) {
      setError('Välj en ZIP-fil');
      return;
    }
    
    if (!window.confirm('Detta kommer att importera/uppdatera Grafisk Manual-data. Vill du fortsätta?')) {
      return;
    }
    
    try {
      setImporting(true);
      setError(null);
      
      const result = await brandGuideService.importData(file);
      
      setError(null);
      alert(`Import slutförd! ${result.sectionsImported} sektioner, ${result.filesImported} filer.`);
      
      // Ladda om data
      fetchSections();
      fetchSettings();
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.message || 'Kunde inte importera Grafisk Manual');
    } finally {
      setImporting(false);
    }
  };

  const fetchSections = useCallback(() => {
    setLoading(true);
    brandGuideService
      .fetchSections(false)
      .then((data) => {
        setSections(data);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || 'Ett fel inträffade.');
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchSettings = useCallback(() => {
    brandGuideService.getSettings()
      .then((data) => {
        setHeaderSettings({
          headerSubtitle: data.headerSubtitle || '',
          headerTitle: data.headerTitle || '',
          headerDescription: data.headerDescription || '',
          footerText: data.footerText || '',
        });
      })
      .catch(() => {});
  }, []);

  const fetchPdfInfo = useCallback(() => {
    brandGuideService.getPdfInfo()
      .then((data) => setPdfInfo(data))
      .catch(() => setPdfInfo({ exists: false }));
  }, []);

  useEffect(() => {
    fetchSections();
    fetchSettings();
    fetchPdfInfo();
  }, [fetchSections, fetchSettings, fetchPdfInfo]);

  const openCreateDialog = () => {
    setEditingSlug(null);
    setFormState(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = useCallback((section: IBrandGuideSection) => {
    setEditingSlug(section.slug);
    setFormState({
      title: section.title,
      slug: section.slug,
      icon: section.icon || ICON_OPTIONS[0].value,
      previewText: section.previewText,
      introText: section.introText || '',
      displayOrder: section.displayOrder ?? 0,
      isPublished: section.isPublished,
      contentHtml: section.contentHtml,
      images: section.images || [],
      cardGradientStart: section.cardGradientStart || '#1B3967',
      cardGradientEnd: section.cardGradientEnd || '#0050E6',
      cardSymbol: section.cardSymbol || '◆',
      cardImage: section.cardImage || '',
    });
    setDialogOpen(true);
  }, []);

  useEffect(() => {
    if (!initialSlug || !sections.length) return;
    const match = sections.find((section) => section.slug === initialSlug);
    if (match) {
      openEditDialog(match);
    }
    onInitialSlugHandled?.();
  }, [initialSlug, sections, openEditDialog, onInitialSlugHandled]);

  // Editor för huvudinnehåll
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'brand-guide-image',
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
      }),
      Placeholder.configure({
        placeholder: 'Börja skriva innehållet här...',
      }),
    ],
    content: formState.contentHtml || '',
    onUpdate: ({ editor }) => {
      setFormState((prev) => ({
        ...prev,
        contentHtml: editor.getHTML(),
      }));
    },
  }, [dialogOpen]);

  // Editor för introtext (vänster sidebar)
  const introEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
      }),
      Placeholder.configure({
        placeholder: 'Skriv introtext för vänster sidebar...',
      }),
    ],
    content: formState.introText || '',
    onUpdate: ({ editor }) => {
      setFormState((prev) => ({
        ...prev,
        introText: editor.getHTML(),
      }));
    },
  }, [dialogOpen]);

  // Refs för att spåra om innehåll har satts (undvik att sätta om vid varje render)
  const contentInitialized = React.useRef(false);
  const introContentInitialized = React.useRef(false);

  // Återställ refs när dialogen stängs
  useEffect(() => {
    if (!dialogOpen) {
      contentInitialized.current = false;
      introContentInitialized.current = false;
    }
  }, [dialogOpen]);

  // Sätt huvudinnehåll endast en gång när dialogen öppnas
  useEffect(() => {
    if (editor && dialogOpen && !contentInitialized.current) {
      editor.commands.setContent(formState.contentHtml || '');
      contentInitialized.current = true;
    }
  }, [editor, dialogOpen, formState.contentHtml]);

  // Sätt introinnehåll endast en gång när dialogen öppnas
  useEffect(() => {
    if (introEditor && dialogOpen && !introContentInitialized.current) {
      introEditor.commands.setContent(formState.introText || '');
      introContentInitialized.current = true;
    }
  }, [introEditor, dialogOpen, formState.introText]);

  const handleInputChange = (
    field: keyof SectionFormState,
    value: string | number | boolean
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!formState.title || !formState.slug || !formState.previewText) {
      setError('Fyll i titel, slug och sammanfattning innan du sparar.');
      return;
    }
    setSaving(true);
    try {
      const sectionData = {
        title: formState.title,
        previewText: formState.previewText,
        introText: formState.introText,
        icon: formState.icon,
        contentHtml: formState.contentHtml,
        displayOrder: formState.displayOrder,
        isPublished: formState.isPublished,
        images: formState.images,
        cardGradientStart: formState.cardGradientStart,
        cardGradientEnd: formState.cardGradientEnd,
        cardSymbol: formState.cardSymbol,
        cardImage: formState.cardImage,
      };

      if (editingSlug) {
        await brandGuideService.updateSection(editingSlug, {
          ...sectionData,
          newSlug: formState.slug,
        });
      } else {
        await brandGuideService.createSection({
          ...sectionData,
          slug: formState.slug,
        });
      }
      setDialogOpen(false);
      setFormState(EMPTY_FORM);
      fetchSections();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte spara sektionen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section: IBrandGuideSection) => {
    if (!window.confirm(`Vill du ta bort sektionen "${section.title}"?`)) {
      return;
    }
    try {
      await brandGuideService.deleteSection(section.slug);
      fetchSections();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ta bort sektionen.');
    }
  };

  const handleTogglePublish = async (
    section: IBrandGuideSection,
    value: boolean
  ) => {
    try {
      await brandGuideService.updateSection(section.slug, {
        isPublished: value,
      });
      fetchSections();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte uppdatera status.');
    }
  };

  // Spara header/footer inställningar
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await brandGuideService.updateSettings(headerSettings);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte spara inställningar.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Ladda upp PDF
  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    const file = event.target.files[0];
    setUploadingPdf(true);
    try {
      await brandGuideService.uploadPdf(file);
      fetchPdfInfo();
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp PDF.');
    } finally {
      setUploadingPdf(false);
      event.target.value = '';
    }
  };

  // Ta bort PDF
  const handleDeletePdf = async () => {
    if (!window.confirm('Vill du ta bort den uppladdade PDF:en?')) return;
    try {
      await brandGuideService.deletePdf();
      fetchPdfInfo();
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ta bort PDF.');
    }
  };

  const [uploadingCardImage, setUploadingCardImage] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!formState.slug) {
      setError('Ange slug innan du laddar upp bilder.');
      return;
    }
    const file = event.target.files[0];
    setUploadingImage(true);
    try {
      const imageUrl = await brandGuideService.uploadImage(formState.slug, file);
      const absoluteUrl = imageUrl.startsWith('http')
        ? imageUrl
        : `${API_BASE_URL}${imageUrl}`;
      editor?.chain().focus().setImage({ src: absoluteUrl }).run();
      setFormState((prev) => ({
        ...prev,
        images: [...prev.images, absoluteUrl],
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp bilden.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Bilduppladdning för introtext
  const handleIntroImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!formState.slug) {
      setError('Ange slug innan du laddar upp bilder.');
      return;
    }
    const file = event.target.files[0];
    setUploadingImage(true);
    try {
      const imageUrl = await brandGuideService.uploadImage(formState.slug, file);
      const absoluteUrl = imageUrl.startsWith('http')
        ? imageUrl
        : `${API_BASE_URL}${imageUrl}`;
      introEditor?.chain().focus().setImage({ src: absoluteUrl }).run();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp bilden.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Bilduppladdning för kortdesign
  const handleCardImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!formState.slug) {
      setError('Ange slug innan du laddar upp bilder.');
      return;
    }
    const file = event.target.files[0];
    setUploadingCardImage(true);
    try {
      const imageUrl = await brandGuideService.uploadImage(formState.slug, file);
      const absoluteUrl = imageUrl.startsWith('http')
        ? imageUrl
        : `${API_BASE_URL}${imageUrl}`;
      setFormState((prev) => ({
        ...prev,
        cardImage: absoluteUrl,
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp bilden.');
    } finally {
      setUploadingCardImage(false);
      event.target.value = '';
    }
  };

  const insertImageFromUrl = () => {
    const url = window.prompt('Ange bildadress (URL):');
    if (!url) return;
    editor?.chain().focus().setImage({ src: url }).run();
  };

  const insertIntroImageFromUrl = () => {
    const url = window.prompt('Ange bildadress (URL):');
    if (!url) return;
    introEditor?.chain().focus().setImage({ src: url }).run();
  };

  // Växla skugga på markerad bild
  const toggleImageShadow = () => {
    if (!editor) return;
    const { state } = editor;
    const { selection } = state;
    const node = state.doc.nodeAt(selection.from);
    
    if (node?.type.name === 'image') {
      const currentClass = node.attrs.class || '';
      const hasShadow = currentClass.includes('with-shadow');
      const newClass = hasShadow 
        ? currentClass.replace('with-shadow', '').trim()
        : (currentClass + ' with-shadow').trim();
      
      editor.chain().focus().updateAttributes('image', { class: newClass }).run();
    } else {
      alert('Markera en bild först för att växla skugga.');
    }
  };

  // Kolla om markerad bild har skugga
  const selectedImageHasShadow = (): boolean => {
    if (!editor) return false;
    const { state } = editor;
    const { selection } = state;
    const node = state.doc.nodeAt(selection.from);
    if (node?.type.name === 'image') {
      return (node.attrs.class || '').includes('with-shadow');
    }
    return false;
  };

  const insertLink = () => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('Ange länkadress:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().unsetLink().run();
      return;
    }
    editor
      ?.chain()
      .focus()
      .setLink({ href: url, target: '_blank' })
      .run();
  };

  const sortedSections = useMemo(
    () =>
      [...sections].sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.title.localeCompare(b.title)
      ),
    [sections]
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: COLORS.olympia,
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Kapra Neue", "Oswald", sans-serif',
            fontWeight: 700,
            fontSize: '2rem',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            mb: 1,
          }}
        >
          Grafisk Manual
        </Typography>
        <Typography sx={{ opacity: 0.9, mb: 2 }}>
          Skapa och redigera sektionerna som visas i den grafiska manualen.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{
            bgcolor: 'white',
            color: COLORS.olympia,
            fontWeight: 600,
            '&:hover': { bgcolor: COLORS.sandLight },
          }}
        >
          Ny sektion
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sidinställningar - Header, Footer, PDF */}
      <Paper sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography
          sx={{
            fontFamily: '"Oswald", sans-serif',
            fontWeight: 600,
            fontSize: '1.1rem',
            color: COLORS.olympia,
            textTransform: 'uppercase',
            mb: 2,
          }}
        >
          Sidinställningar
        </Typography>
        
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Header undertitel"
              value={headerSettings.headerSubtitle}
              onChange={(e) => setHeaderSettings(prev => ({ ...prev, headerSubtitle: e.target.value }))}
              fullWidth
              size="small"
              placeholder="t.ex. FONTANA • EST. 1978"
            />
            <TextField
              label="Header titel"
              value={headerSettings.headerTitle}
              onChange={(e) => setHeaderSettings(prev => ({ ...prev, headerTitle: e.target.value }))}
              fullWidth
              size="small"
              placeholder="t.ex. Grafisk Manual"
            />
          </Stack>
          <TextField
            label="Header beskrivning"
            value={headerSettings.headerDescription}
            onChange={(e) => setHeaderSettings(prev => ({ ...prev, headerDescription: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Beskrivande text som visas i headern"
          />
          <TextField
            label="Footer text"
            value={headerSettings.footerText}
            onChange={(e) => setHeaderSettings(prev => ({ ...prev, footerText: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Text som visas längst ned på sidan"
          />
          
          <Divider sx={{ my: 1 }} />
          
          {/* PDF-uppladdning */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.olympia }}>
                Komplett PDF
              </Typography>
              {pdfInfo?.exists ? (
                <Typography variant="body2" color="text.secondary">
                  Uppladdad: {pdfInfo.size ? `${(pdfInfo.size / 1024 / 1024).toFixed(2)} MB` : ''} 
                  {pdfInfo.updatedAt && ` • ${new Date(pdfInfo.updatedAt).toLocaleDateString('sv-SE')}`}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Ingen PDF uppladdad ännu
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                disabled={uploadingPdf}
              >
                {uploadingPdf ? 'Laddar upp...' : 'Ladda upp PDF'}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handlePdfUpload}
                />
              </Button>
              {pdfInfo?.exists && (
                <IconButton
                  size="small"
                  onClick={handleDeletePdf}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              sx={{
                bgcolor: COLORS.olympia,
                '&:hover': { bgcolor: COLORS.originalet },
              }}
            >
              {savingSettings ? 'Sparar...' : 'Spara inställningar'}
            </Button>
          </Box>

          {/* Export/Import sektion */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Export / Import
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Exportera eller importera Grafisk Manual-data inklusive alla sektioner och bilder.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting || importing}
              sx={{ borderColor: COLORS.olympia, color: COLORS.olympia }}
            >
              {exporting ? 'Exporterar...' : 'Exportera'}
            </Button>
            <Button
              variant="outlined"
              startIcon={importing ? <CircularProgress size={16} /> : <UploadIcon />}
              component="label"
              disabled={exporting || importing}
              sx={{ borderColor: COLORS.olympia, color: COLORS.olympia }}
            >
              {importing ? 'Importerar...' : 'Importera'}
              <input
                type="file"
                accept=".zip"
                hidden
                onChange={handleImport}
              />
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Sections list */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <List disablePadding>
          {sortedSections.map((section, index) => (
            <React.Fragment key={section._id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 2,
                  px: 3,
                  '&:hover': { bgcolor: COLORS.sandLight },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: COLORS.olympia,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    mr: 2,
                    fontSize: '1.1rem',
                  }}
                >
                  {section.displayOrder}
                </Box>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography sx={{ fontWeight: 600, color: COLORS.olympia }}>
                        {section.title}
                      </Typography>
                      <Chip
                        label={section.slug}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      {!section.isPublished && (
                        <Chip
                          label="Utkast"
                          size="small"
                          sx={{
                            bgcolor: 'warning.light',
                            color: 'warning.dark',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Stack>
                  }
                  secondary={section.previewText}
                  secondaryTypographyProps={{
                    sx: {
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 500,
                    },
                  }}
                />
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={section.isPublished}
                          onChange={(e) =>
                            handleTogglePublish(section, e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          Publicerad
                        </Typography>
                      }
                      labelPlacement="start"
                    />
                    <Tooltip title="Redigera">
                      <IconButton
                        onClick={() => openEditDialog(section)}
                        sx={{ color: COLORS.olympia }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ta bort">
                      <IconButton
                        onClick={() => handleDelete(section)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
          {!sortedSections.length && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Det finns inga sektioner ännu. Klicka på "Ny sektion" för att skapa den första.
              </Typography>
            </Box>
          )}
        </List>
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: COLORS.olympia,
            color: 'white',
            fontFamily: '"Kapra Neue", "Oswald", sans-serif',
            fontWeight: 600,
            fontSize: '1.5rem',
            textTransform: 'uppercase',
          }}
        >
          {editingSlug ? 'Redigera sektion' : 'Ny sektion'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Basic info row */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Titel"
                value={formState.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Slug (URL-vänligt namn)"
                value={formState.slug}
                onChange={(e) =>
                  handleInputChange(
                    'slug',
                    e.target.value.toLowerCase().replace(/\s+/g, '-')
                  )
                }
                fullWidth
                required
                helperText="Används i webbadressen, t.ex. 'logotyp'"
              />
            </Stack>

            {/* Description and settings */}
            <TextField
              label="Kort beskrivning (visas på korten)"
              value={formState.previewText}
              multiline
              rows={2}
              onChange={(e) => handleInputChange('previewText', e.target.value)}
              fullWidth
              required
            />

            {/* Introtext Editor */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1.5, color: COLORS.olympia }}
              >
                Introtext (visas i vänster sidebar)
              </Typography>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Intro Editor Toolbar */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: COLORS.sandLight,
                  }}
                >
                  <Tooltip title="Ångra">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().undo().run()}
                      disabled={!introEditor?.can().undo()}
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Gör om">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().redo().run()}
                      disabled={!introEditor?.can().redo()}
                    >
                      <RedoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  
                  {/* Rubriker för intro */}
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={
                      introEditor?.isActive('heading', { level: 1 })
                        ? 'h1'
                        : introEditor?.isActive('heading', { level: 2 })
                        ? 'h2'
                        : introEditor?.isActive('heading', { level: 3 })
                        ? 'h3'
                        : 'p'
                    }
                    onChange={(_, value) => {
                      if (value === 'p') {
                        introEditor?.chain().focus().setParagraph().run();
                      } else if (value === 'h1') {
                        introEditor?.chain().focus().toggleHeading({ level: 1 }).run();
                      } else if (value === 'h2') {
                        introEditor?.chain().focus().toggleHeading({ level: 2 }).run();
                      } else if (value === 'h3') {
                        introEditor?.chain().focus().toggleHeading({ level: 3 }).run();
                      }
                    }}
                  >
                    <ToggleButton value="p" sx={{ px: 1 }}>
                      <Typography variant="body2" fontSize="0.7rem">P</Typography>
                    </ToggleButton>
                    <ToggleButton value="h1" sx={{ px: 1 }}>
                      <Typography variant="body2" fontSize="0.7rem" fontWeight={700}>H1</Typography>
                    </ToggleButton>
                    <ToggleButton value="h2" sx={{ px: 1 }}>
                      <Typography variant="body2" fontSize="0.7rem" fontWeight={700}>H2</Typography>
                    </ToggleButton>
                    <ToggleButton value="h3" sx={{ px: 1 }}>
                      <Typography variant="body2" fontSize="0.7rem" fontWeight={700}>H3</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Fet">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().toggleBold().run()}
                      color={introEditor?.isActive('bold') ? 'primary' : 'default'}
                    >
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kursiv">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().toggleItalic().run()}
                      color={introEditor?.isActive('italic') ? 'primary' : 'default'}
                    >
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Understruken">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().toggleUnderline().run()}
                      color={introEditor?.isActive('underline') ? 'primary' : 'default'}
                    >
                      <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Punktlista">
                    <IconButton
                      size="small"
                      onClick={() => introEditor?.chain().focus().toggleBulletList().run()}
                      color={introEditor?.isActive('bulletList') ? 'primary' : 'default'}
                    >
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Infoga länk">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const url = window.prompt('Länkadress:');
                        if (url) {
                          introEditor?.chain().focus().setLink({ href: url, target: '_blank' }).run();
                        }
                      }}
                      color={introEditor?.isActive('link') ? 'primary' : 'default'}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Infoga bild från URL">
                    <IconButton size="small" onClick={insertIntroImageFromUrl}>
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploadingImage || !formState.slug}
                    sx={{ ml: 1 }}
                  >
                    Ladda upp bild
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleIntroImageUpload}
                    />
                  </Button>
                </Box>
                <Box sx={{ minHeight: 150, maxHeight: 250, overflowY: 'auto', p: 1.5 }}>
                  <EditorContent editor={introEditor} />
                </Box>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Längre beskrivning som visas i den sandfärgade spalten på detaljsidan
              </Typography>
            </Box>

            {/* Kortdesign */}
            <Box sx={{ p: 2, bgcolor: COLORS.sandLight, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: COLORS.olympia }}>
                Kortdesign (övre delen)
              </Typography>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <InputLabel shrink sx={{ mb: 0.5 }}>Gradient startfärg</InputLabel>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <input
                      type="color"
                      value={formState.cardGradientStart}
                      onChange={(e) => handleInputChange('cardGradientStart', e.target.value)}
                      style={{ width: 50, height: 36, border: 'none', cursor: 'pointer' }}
                    />
                    <TextField
                      value={formState.cardGradientStart}
                      onChange={(e) => handleInputChange('cardGradientStart', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <InputLabel shrink sx={{ mb: 0.5 }}>Gradient slutfärg</InputLabel>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <input
                      type="color"
                      value={formState.cardGradientEnd}
                      onChange={(e) => handleInputChange('cardGradientEnd', e.target.value)}
                      style={{ width: 50, height: 36, border: 'none', cursor: 'pointer' }}
                    />
                    <TextField
                      value={formState.cardGradientEnd}
                      onChange={(e) => handleInputChange('cardGradientEnd', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Symbol/tecken (visas på kortet)"
                  value={formState.cardSymbol}
                  onChange={(e) => handleInputChange('cardSymbol', e.target.value)}
                  sx={{ width: { xs: '100%', md: 200 } }}
                  size="small"
                  helperText="T.ex. F, Aa, ●, ◆, ♥"
                />
                <Box sx={{ flex: 1 }}>
                  <InputLabel shrink sx={{ mb: 0.5 }}>Kortbild (alternativ till symbol)</InputLabel>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      value={formState.cardImage}
                      onChange={(e) => handleInputChange('cardImage', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="URL eller ladda upp..."
                    />
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      disabled={uploadingCardImage || !formState.slug}
                      sx={{ minWidth: 'auto', px: 2, height: 40 }}
                    >
                      {uploadingCardImage ? '...' : <CloudUploadIcon />}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleCardImageUpload}
                      />
                    </Button>
                    {formState.cardImage && (
                      <Tooltip title="Ta bort bild">
                        <IconButton
                          size="small"
                          onClick={() => handleInputChange('cardImage', '')}
                          sx={{ height: 40 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Lämna tom för att använda symbolen
                  </Typography>
                </Box>
              </Stack>

              {/* Förhandsvisning av kort */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                  Förhandsvisning:
                </Typography>
                <Box
                  sx={{
                    width: 200,
                    height: 100,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${formState.cardGradientStart} 0%, ${formState.cardGradientEnd} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {formState.cardImage ? (
                    <Box
                      component="img"
                      src={formState.cardImage}
                      alt="Kort bild"
                      sx={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '2.5rem',
                        fontWeight: 700,
                        color: 'white',
                        fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                      }}
                    >
                      {formState.cardSymbol || '◆'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <InputLabel shrink sx={{ mb: 0.5 }}>
                  Ikon
                </InputLabel>
                <Select
                  fullWidth
                  value={formState.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  size="small"
                >
                  {ICON_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <TextField
                label="Ordning"
                type="number"
                value={formState.displayOrder}
                onChange={(e) =>
                  handleInputChange('displayOrder', Number(e.target.value))
                }
                sx={{ width: 120 }}
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.isPublished}
                    onChange={(e) =>
                      handleInputChange('isPublished', e.target.checked)
                    }
                  />
                }
                label="Publicerad"
                sx={{ ml: 2 }}
              />
            </Stack>

            {/* Rich Text Editor */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1.5, color: COLORS.olympia }}
              >
                Innehåll
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {/* Editor Toolbar */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    p: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: COLORS.sandLight,
                  }}
                >
                  {/* Undo/Redo */}
                  <Tooltip title="Ångra">
                    <IconButton
                      size="small"
                      onClick={() => editor?.chain().focus().undo().run()}
                      disabled={!editor?.can().undo()}
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Gör om">
                    <IconButton
                      size="small"
                      onClick={() => editor?.chain().focus().redo().run()}
                      disabled={!editor?.can().redo()}
                    >
                      <RedoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  {/* Headings */}
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={
                      editor?.isActive('heading', { level: 1 })
                        ? 'h1'
                        : editor?.isActive('heading', { level: 2 })
                        ? 'h2'
                        : editor?.isActive('heading', { level: 3 })
                        ? 'h3'
                        : 'p'
                    }
                    onChange={(_, value) => {
                      if (value === 'p') {
                        editor?.chain().focus().setParagraph().run();
                      } else if (value === 'h1') {
                        editor?.chain().focus().toggleHeading({ level: 1 }).run();
                      } else if (value === 'h2') {
                        editor?.chain().focus().toggleHeading({ level: 2 }).run();
                      } else if (value === 'h3') {
                        editor?.chain().focus().toggleHeading({ level: 3 }).run();
                      }
                    }}
                  >
                    <ToggleButton value="p" sx={{ px: 1.5 }}>
                      <Typography variant="body2">P</Typography>
                    </ToggleButton>
                    <ToggleButton value="h1" sx={{ px: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>
                        H1
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="h2" sx={{ px: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>
                        H2
                      </Typography>
                    </ToggleButton>
                    <ToggleButton value="h3" sx={{ px: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>
                        H3
                      </Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  {/* Text formatting */}
                  <Tooltip title="Fet">
                    <IconButton
                      size="small"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                      color={editor?.isActive('bold') ? 'primary' : 'default'}
                    >
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kursiv">
                    <IconButton
                      size="small"
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                      color={editor?.isActive('italic') ? 'primary' : 'default'}
                    >
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Understruken">
                    <IconButton
                      size="small"
                      onClick={() => editor?.chain().focus().toggleUnderline().run()}
                      color={editor?.isActive('underline') ? 'primary' : 'default'}
                    >
                      <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  {/* Alignment */}
                  <Tooltip title="Vänsterjustera">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign('left').run()
                      }
                      color={
                        editor?.isActive({ textAlign: 'left' }) ? 'primary' : 'default'
                      }
                    >
                      <FormatAlignLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Centrera">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign('center').run()
                      }
                      color={
                        editor?.isActive({ textAlign: 'center' }) ? 'primary' : 'default'
                      }
                    >
                      <FormatAlignCenterIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Högerjustera">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign('right').run()
                      }
                      color={
                        editor?.isActive({ textAlign: 'right' }) ? 'primary' : 'default'
                      }
                    >
                      <FormatAlignRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  {/* Lists */}
                  <Tooltip title="Punktlista">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                      color={editor?.isActive('bulletList') ? 'primary' : 'default'}
                    >
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Numrerad lista">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                      color={editor?.isActive('orderedList') ? 'primary' : 'default'}
                    >
                      <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Citat">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().toggleBlockquote().run()
                      }
                      color={editor?.isActive('blockquote') ? 'primary' : 'default'}
                    >
                      <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Horisontell linje">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor?.chain().focus().setHorizontalRule().run()
                      }
                    >
                      <HorizontalRuleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  {/* Link and Image */}
                  <Tooltip title="Infoga länk">
                    <IconButton
                      size="small"
                      onClick={insertLink}
                      color={editor?.isActive('link') ? 'primary' : 'default'}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Infoga bild från URL">
                    <IconButton size="small" onClick={insertImageFromUrl}>
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploadingImage || !formState.slug}
                    sx={{ ml: 1 }}
                  >
                    {uploadingImage ? 'Laddar...' : 'Ladda upp bild'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Button>
                  <Tooltip title="Växla skugga på markerad bild">
                    <IconButton
                      size="small"
                      onClick={toggleImageShadow}
                      color={selectedImageHasShadow() ? 'primary' : 'default'}
                      sx={{ ml: 1 }}
                    >
                      <FilterDramaIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Editor Content */}
                <Box
                  sx={{
                    minHeight: 400,
                    maxHeight: 500,
                    overflowY: 'auto',
                    p: 2,
                  }}
                >
                  <EditorContent editor={editor} />
                </Box>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Tips: Använd H1 för huvudrubriker, H2 för underrubriker. Bilder kan placeras var som helst i texten.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: COLORS.olympia, '&:hover': { bgcolor: COLORS.originalet } }}
          >
            {saving ? 'Sparar...' : 'Spara sektion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global editor styles */}
      <style>
        {`
          .ProseMirror {
            outline: none;
            min-height: 380px;
          }
          .ProseMirror p {
            margin: 0 0 1rem;
            line-height: 1.7;
          }
          .ProseMirror h1 {
            font-family: 'Kapra Neue', 'Oswald', sans-serif;
            font-weight: 700;
            font-size: 2rem;
            color: ${COLORS.olympia};
            text-transform: uppercase;
            letter-spacing: 0.02em;
            margin: 1.5rem 0 0.75rem;
          }
          .ProseMirror h1:first-child {
            margin-top: 0;
          }
          .ProseMirror h2 {
            font-family: 'Kapra Neue', 'Oswald', sans-serif;
            font-weight: 600;
            font-size: 1.5rem;
            color: ${COLORS.olympia};
            text-transform: uppercase;
            letter-spacing: 0.02em;
            margin: 1.25rem 0 0.5rem;
          }
          .ProseMirror h3 {
            font-weight: 600;
            font-size: 1.2rem;
            color: ${COLORS.olympia};
            margin: 1rem 0 0.5rem;
          }
          .ProseMirror ul,
          .ProseMirror ol {
            padding-left: 1.5rem;
            margin-bottom: 1rem;
          }
          .ProseMirror li {
            margin-bottom: 0.25rem;
          }
          .ProseMirror blockquote {
            border-left: 4px solid ${COLORS.originalet};
            padding-left: 1rem;
            margin: 1rem 0;
            font-style: italic;
            color: #666;
          }
          .ProseMirror hr {
            border: none;
            border-top: 2px solid ${COLORS.sand};
            margin: 2rem 0;
          }
          .ProseMirror img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 1rem 0;
          }
          .ProseMirror img.with-shadow {
            box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          }
          .ProseMirror a {
            color: ${COLORS.originalet};
            text-decoration: underline;
          }
          .ProseMirror p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: #adb5bd;
            pointer-events: none;
            height: 0;
          }
        `}
      </style>
    </Box>
  );
};

export default BrandGuideManagementPage;
